import { fetchAnalyticOptions, SummaryType } from "@ls/grouping-service";
import { exchange } from "@ls/js-fetch";
import GroupingInput from "state/Constants/GroupingInput";
import ScaleType from "state/Constants/ScaleType";
import SelectionState from "state/selections/SelectionState";
import {
  selectGetGroupingParams,
  selectSideBySideValue,
  selectIsDisableBenchmarkWorkspace,
  selectScenarioValue,
  selectBenchmarkScenarioValue,
} from "state/selections/selectors";
import { splitByCalcType, getRatioTypeDefinitions } from "state/util";
import scope from "./scope";
import { selectDefinitions } from "./selectors";

export const DELETE_DEFINITION = `${scope}/DELETE_DEFINITION`;
export const SET_DEFINITION = `${scope}/SET_DEFINITION`;
export const setDefinition = (idx, definition) => ({
  type: SET_DEFINITION,
  idx,
  definition,
});
export const SET_DEFINITIONS = `${scope}/SET_DEFINITIONS`;
export const setDefinitions = (definitions) => ({
  type: SET_DEFINITIONS,
  definitions,
});

export const REQUEST_ANALYTIC_VALUES = `${scope}/REQUEST_ANALYTIC_VALUES`;
export const RECEIVE_ANALYTIC_VALUES = `${scope}/RECEIVE_ANALYTIC_VALUES`;
export const requestAnalyticsValues = () => {
  return async (dispatch) => {
    dispatch({ type: REQUEST_ANALYTIC_VALUES });
    dispatch({
      type: RECEIVE_ANALYTIC_VALUES,
      data: Object.values((await fetchAnalyticOptions()) || {}),
    });
  };
};

export const REQUEST_ANALYTICS_DATA = `${scope}/REQUEST_ANALYTICS_DATA`;
export const RECEIVE_ANALYTICS_DATA = `${scope}/RECEIVE_ANALYTICS_DATA`;

export const REQUEST_ANALYTICS_RATIO_DATA = `${scope}/REQUEST_ANALYTICS_RATIO_DATA`;
export const RECEIVE_ANALYTICS_RATIO_DATA = `${scope}/RECEIVE_ANALYTICS_RATIO_DATA`;

export const REQUEST_ANALYTICS_SBS_DATA = `${scope}/REQUEST_ANALYTICS_SBS_DATA`;
export const RECEIVE_ANALYTICS_SBS_DATA = `${scope}/RECEIVE_ANALYTICS_SBS_DATA`;

export const requestGridData = () => {
  return async (dispatch, getState) => {
    const state = getState();

    const definitions = selectDefinitions(SelectionState.COMMITTED)(state);

    if (!definitions?.length) {
      return;
    }

    const [absDefinitions, bmkDefinitions, relDefinitions] =
      splitByCalcType(definitions);
    const getGroupingParams = selectGetGroupingParams(SelectionState.COMMITTED)(
      state,
    );
    const scenarioValue = selectScenarioValue(SelectionState.COMMITTED)(state);
    const sideBySideValue = selectSideBySideValue(SelectionState.COMMITTED)(
      state,
    );
    const isDisableBenchmarkWorkspace = selectIsDisableBenchmarkWorkspace(
      SelectionState.COMMITTED,
    )(state);
    const benchmarkScenarioValue = selectBenchmarkScenarioValue(
      SelectionState.COMMITTED,
    )(state);
    const request = async (summary, definitions, benchesToo) => {
      dispatch({ type: REQUEST_ANALYTICS_DATA, summary });

      // Get percentile definitions (support both named and numeric values)
      const percentileDefinitions = definitions.filter(({ scaleType }) => {
        return scaleType === "25" || scaleType === "50" || scaleType === "75";
      });

      // Get non-percentile and non-ratio definitions for regular analytics
      const regularDefinitions = definitions.filter(({ scaleType }) => {
        return (
          scaleType !== "25" &&
          scaleType !== "50" &&
          scaleType !== "75" &&
          scaleType !== ScaleType.Ratio
        );
      });

      let params = getGroupingParams({
        summary,
        benchesToo,
        analytics: [
          ...new Set(regularDefinitions.map(({ analytic }) => analytic)),
        ],
        inputs: !!definitions.find(
          (def) => def.scaleType === ScaleType.WeightedAverage,
        )
          ? [GroupingInput.BUCKETS]
          : [],
        ...(percentileDefinitions.length > 0 && {
          percentile_analytics: [
            ...new Set(percentileDefinitions.map(({ analytic }) => analytic)),
          ],
          percentiles: [
            ...new Set(percentileDefinitions.map(({ scaleType }) => scaleType)),
          ],
        }),
      });

      if (!!scenarioValue && !sideBySideValue) {
        params = { ...params, workspace: scenarioValue };
      }

      if (!isDisableBenchmarkWorkspace) {
        params = {
          ...params,
          alt_bench_workspace: !!benchmarkScenarioValue
            ? benchmarkScenarioValue
            : "",
        };
      }

      dispatch({
        type: RECEIVE_ANALYTICS_DATA,
        summary,
        data: (
          await exchange({
            params,
            cancelId: `data-${scope}-${summary}`,
            url: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
          })
        )?.data,
      });

      if (!!scenarioValue && !!sideBySideValue) {
        params = { ...params, workspace: scenarioValue };
        dispatch({ type: REQUEST_ANALYTICS_SBS_DATA, summary });
        dispatch({
          type: RECEIVE_ANALYTICS_SBS_DATA,
          summary,
          data: (
            await exchange({
              url: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
              params,
              cancelId: `data-sbs-${scope}-${summary}`,
            })
          )?.data,
        });
      }
    };

    const requestRatio = async (summary, definitions, benchesToo) => {
      dispatch({ type: REQUEST_ANALYTICS_RATIO_DATA, summary });

      let params = getGroupingParams({
        summary,
        benchesToo,
        analytics: [...new Set(definitions.map(({ analytic }) => analytic))],
        inputs: !!definitions.find(
          (def) => def.scaleType === ScaleType.WeightedAverage,
        )
          ? [GroupingInput.BUCKETS]
          : [],
      });

      params.relative = true;
      params.relativeTypes = "RATIO";

      if (!isDisableBenchmarkWorkspace) {
        params = {
          ...params,
          alt_bench_workspace: !!benchmarkScenarioValue
            ? benchmarkScenarioValue
            : "",
        };
      }

      dispatch({
        type: RECEIVE_ANALYTICS_RATIO_DATA,
        summary,
        data: (
          await exchange({
            params,
            cancelId: `data-ratio-${scope}-${summary}`,
            url: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
          })
        )?.data,
      });
    };

    if (absDefinitions.length || bmkDefinitions.length) {
      request(
        SummaryType.ABSOLUTE,
        [...absDefinitions, ...bmkDefinitions],
        bmkDefinitions.length > 0,
      ).then();
    }

    if (relDefinitions.length) {
      request(SummaryType.RELATIVE, relDefinitions, true).then();
    }

    if (getRatioTypeDefinitions(definitions).length && relDefinitions.length) {
      requestRatio(
        SummaryType.RELATIVE,
        getRatioTypeDefinitions(definitions),
        true,
      ).then();
    }
  };
};
